/**
 * DataGrid - Sortable, filterable, paginated table
 *
 * Usage:
 *   <DataGrid
 *     title="Workout History"
 *     data={{
 *       columns: [
 *         { key: 'date', label: 'Date', sortable: true },
 *         { key: 'type', label: 'Type', filterable: true },
 *         { key: 'duration', label: 'Duration', align: 'right', sortable: true },
 *       ],
 *       rows: [
 *         { date: '2024-02-10', type: 'Running', duration: '32:00' },
 *         ...
 *       ],
 *       pageSize: 10
 *     }}
 *   />
 */

import { useState, useMemo } from 'react';
import type { BlockProps, DataGridData } from './types';
import './blocks.css';

export interface DataGridProps extends BlockProps<DataGridData> {
  /** Enable row selection */
  selectable?: boolean;
  /** Row click handler */
  onRowClick?: (row: Record<string, string | number | boolean>, index: number) => void;
  /** Page change handler */
  onPageChange?: (page: number) => void;
}

type SortDirection = 'asc' | 'desc' | null;

export function DataGrid({
  data,
  title,
  selectable = false,
  onRowClick,
  onPageChange,
  className,
}: DataGridProps) {
  const { columns = [], rows = [], totalRows, page: initialPage = 1, pageSize = 10 } = data ?? {};

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Filter rows
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = String(row[key] || '').toLowerCase();
        return cellValue.includes(filterValue.toLowerCase());
      });
    });
  }, [rows, filters]);

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortColumn, sortDirection]);

  // Paginate rows
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  const totalPages = Math.ceil((totalRows ?? sortedRows.length) / pageSize);

  const handleSort = (columnKey: string) => {
    const column = columns.find(c => c.key === columnKey);
    if (!column?.sortable) return;

    if (sortColumn === columnKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') setSortColumn(null);
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleFilter = (columnKey: string, value: string) => {
    setFilters(prev => ({ ...prev, [columnKey]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onPageChange?.(page);
  };

  const toggleRowSelection = (index: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const hasFilters = columns.some(c => c.filterable);

  if (!rows || rows.length === 0) {
    return (
      <div className={`block-table ${className || ''}`}>
        {title && <div className="block-table-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={`block-table data-grid ${className || ''}`}>
      {title && <div className="block-table-title">{title}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: '40px', padding: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedRows.length && paginatedRows.length > 0}
                    onChange={() => {
                      if (selectedRows.size === paginatedRows.length) {
                        setSelectedRows(new Set());
                      } else {
                        setSelectedRows(new Set(paginatedRows.map((_, i) => i)));
                      }
                    }}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align || 'left',
                    width: col.width,
                    cursor: col.sortable ? 'pointer' : undefined,
                    userSelect: col.sortable ? 'none' : undefined,
                  }}
                  onClick={() => handleSort(col.key)}
                  onKeyDown={col.sortable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSort(col.key);
                    }
                  } : undefined}
                  tabIndex={col.sortable ? 0 : undefined}
                  role={col.sortable ? 'button' : undefined}
                  aria-label={col.sortable ? `Sort by ${col.label}` : undefined}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                    {col.label}
                    {col.sortable && (
                      <span style={{ opacity: sortColumn === col.key ? 1 : 0.3, fontSize: '10px' }}>
                        {sortColumn === col.key && sortDirection === 'asc' ? '▲' :
                         sortColumn === col.key && sortDirection === 'desc' ? '▼' : '⇅'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
            {hasFilters && (
              <tr>
                {selectable && <th />}
                {columns.map(col => (
                  <th key={`filter-${col.key}`} style={{ padding: '4px 8px' }}>
                    {col.filterable ? (
                      <input
                        type="text"
                        placeholder={`Filter ${col.label}...`}
                        value={filters[col.key] || ''}
                        onChange={e => handleFilter(col.key, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '4px 8px',
                          fontSize: '12px',
                          border: '1px solid var(--color-border)',
                          borderRadius: '4px',
                          background: 'var(--color-surface)',
                          color: 'var(--color-text)',
                        }}
                        aria-label={`Filter by ${col.label}`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {paginatedRows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row, rowIndex);
                  }
                } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                aria-label={onRowClick ? `Select row ${rowIndex + 1}` : undefined}
                style={{
                  cursor: onRowClick ? 'pointer' : undefined,
                  background: selectedRows.has(rowIndex) ? 'var(--color-surface-muted)' : undefined,
                }}
              >
                {selectable && (
                  <td style={{ padding: '8px' }}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(rowIndex)}
                      onChange={() => toggleRowSelection(rowIndex)}
                      onClick={e => e.stopPropagation()}
                      aria-label={`Select row ${rowIndex + 1}`}
                    />
                  </td>
                )}
                {columns.map(col => (
                  <td
                    key={col.key}
                    style={{ textAlign: col.align || 'left' }}
                  >
                    {formatCellValue(row[col.key], col.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderTop: '1px solid var(--color-border)',
          fontSize: '13px',
          color: 'var(--color-text-muted)',
        }}>
          <span>
            Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalRows ?? sortedRows.length)} of {totalRows ?? sortedRows.length}
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '4px 8px',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                background: 'var(--color-surface)',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.5 : 1,
              }}
              aria-label="Previous page"
            >
              ←
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    background: currentPage === pageNum ? 'var(--color-primary)' : 'var(--color-surface)',
                    color: currentPage === pageNum ? 'white' : 'var(--color-text)',
                    cursor: 'pointer',
                    minWidth: '32px',
                  }}
                  aria-label={`Page ${pageNum}`}
                  aria-current={currentPage === pageNum ? 'page' : undefined}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '4px 8px',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                background: 'var(--color-surface)',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.5 : 1,
              }}
              aria-label="Next page"
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCellValue(value: string | number | boolean | undefined, type?: string): React.ReactNode {
  if (value === undefined || value === null) return '—';

  switch (type) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    case 'date':
      return new Date(String(value)).toLocaleDateString();
    case 'badge':
      return (
        <span style={{
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 500,
          background: 'var(--color-surface-muted)',
          color: 'var(--color-text)',
        }}>
          {String(value)}
        </span>
      );
    default:
      return String(value);
  }
}

export default DataGrid;
